"use client";

import React, { useMemo, useState } from "react";
import Script from "next/script";
import { Input, Select, CalendlyWidget } from "@/components/ui";
import { env } from "@/lib/environment";
import { useGeoLocation } from "@/lib/hooks/useGeoLocation";
import { useUTMContext } from "@/lib/context/UTMProvider";
import { trackDemoLead } from "@/lib/tracking/ga";

const fieldClassName = "rounded-lg border-[#D7D9DE] font-normal";

type FormState = {
  current_tool: string;
  screens: string;
  sites: string;
  monthly_spend: string;
  email: string;
};

const initialForm: FormState = {
  current_tool: "",
  screens: "",
  sites: "",
  monthly_spend: "",
  email: "",
};

type SwitchRoiFormProps = {
  competitorValue: string;
  competitorLabel: string;
  formName: string;
};

function mapScreensToCalendly(screens: string): string | null {
  const count = Number(screens);
  if (!Number.isFinite(count) || count < 1) return null;
  if (count <= 5) return "1-5";
  if (count <= 20) return "6-20";
  if (count <= 200) return "21-200";
  return "201+";
}

export function SwitchRoiForm({
  competitorValue,
  competitorLabel,
  formName,
}: SwitchRoiFormProps) {
  const { geoData } = useGeoLocation();
  const { getAllTrackingData } = useUTMContext();
  const [formData, setFormData] = useState<FormState>(initialForm);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = event.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const toolLabel =
    formData.current_tool === competitorValue
      ? competitorLabel
      : formData.current_tool === "other"
        ? "Other"
        : "";

  const helpWith = useMemo(() => {
    if (!toolLabel || !formData.screens || !formData.sites || !formData.monthly_spend) {
      return `Switch ROI estimate — migrating from ${competitorLabel}`;
    }
    return [
      `Switch ROI estimate — migrating from ${toolLabel}`,
      `${formData.screens} screens`,
      `${formData.sites} sites`,
      `$${formData.monthly_spend}/mo current spend`,
    ].join(" · ");
  }, [
    competitorLabel,
    formData.monthly_spend,
    formData.screens,
    formData.sites,
    toolLabel,
  ]);

  const isComplete =
    formData.current_tool.trim().length > 0 &&
    formData.screens.trim().length > 0 &&
    formData.sites.trim().length > 0 &&
    formData.monthly_spend.trim().length > 0 &&
    formData.email.trim().length > 0;

  const handleSubmitDataOnZoho = async () => {
    try {
      const grecaptcha = (
        window as Window & {
          grecaptcha?: { execute: (key: string, opts: { action: string }) => Promise<string> };
        }
      ).grecaptcha;

      if (!grecaptcha?.execute) {
        throw new Error("reCAPTCHA not loaded. Please refresh the page and try again.");
      }

      await new Promise(resolve => setTimeout(resolve, 100));

      const token = await grecaptcha.execute(env.NEXT_PUBLIC_GRECAPTCHA_KEY, {
        action: "submit",
      });

      if (!token) {
        throw new Error("Failed to get reCAPTCHA token. Please try again.");
      }

      const details = {
        name: formData.email.split("@")[0] || "Switch ROI",
        email: formData.email,
        phone: "-",
        token,
        leads_source: "Calendly",
        company_name: formData.email.split("@")[1] || "",
        description: [
          `Switch ROI estimate (Pickcel vs ${competitorLabel})`,
          `Current tool: ${toolLabel || competitorLabel}`,
          `Screens managed: ${formData.screens}`,
          `Number of sites: ${formData.sites}`,
          `Current monthly spend (USD): ${formData.monthly_spend}`,
        ].join(" | "),
        screen_count: formData.screens,
        form_name: formName,
        submitted_at: new Date().toISOString(),
        address: {
          country_code: geoData?.countryCode,
          city: geoData?.city,
          region: geoData?.region,
        },
        ...getAllTrackingData(),
      };

      const res = await fetch(env.NEXT_PUBLIC_WIDGET_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ details }),
      });

      if (!res.ok) {
        throw new Error(`HTTP error! status: ${res.status}`);
      }

      const result = await res.json();
      if (!result.success) {
        throw new Error(result.message || "Submission failed");
      }

      trackDemoLead();
    } catch (error) {
      console.error("Switch ROI form submission error:", error);
    }
  };

  return (
    <>
      <Script
        src={`https://www.google.com/recaptcha/api.js?render=${env.NEXT_PUBLIC_GRECAPTCHA_KEY}`}
        strategy="afterInteractive"
      />

      <form
        data-lead="switch-roi"
        data-backend-status="connected"
        className="grid grid-cols-1 gap-3 md:grid-cols-2"
        onSubmit={event => event.preventDefault()}
      >
        <Select
          name="current_tool"
          label="Current tool"
          required
          value={formData.current_tool}
          onChange={handleChange}
          wrapperClassName="md:col-span-2"
          selectClassName={`${fieldClassName} invalid:text-gray-400 [&_option]:text-text-primary`}
        >
          <option value="" disabled>
            Select your current tool
          </option>
          <option value={competitorValue}>{competitorLabel}</option>
          <option value="other">Other</option>
        </Select>

        <Input
          name="screens"
          type="number"
          min={1}
          required
          inputMode="numeric"
          placeholder="Screens managed"
          aria-label="Screens managed"
          value={formData.screens}
          onChange={handleChange}
          inputClassName={fieldClassName}
        />
        <Input
          name="sites"
          type="number"
          min={1}
          required
          inputMode="numeric"
          placeholder="Number of sites"
          aria-label="Number of sites"
          value={formData.sites}
          onChange={handleChange}
          inputClassName={fieldClassName}
        />
        <Input
          name="monthly_spend"
          type="number"
          min={0}
          required
          inputMode="decimal"
          placeholder="Current monthly spend (USD)"
          aria-label="Current monthly spend (USD)"
          value={formData.monthly_spend}
          onChange={handleChange}
          inputClassName={fieldClassName}
        />
        <Input
          name="email"
          type="email"
          required
          autoComplete="email"
          placeholder="Work email"
          aria-label="Work email"
          value={formData.email}
          onChange={handleChange}
          inputClassName={fieldClassName}
        />

        <div className="md:col-span-2">
          <CalendlyWidget
            buttonText="Send My Estimate"
            buttonVariant="primary"
            buttonSize="md"
            buttonClassName="w-full"
            buttonTrackingId="cta-book-a-demo"
            disabled={!isComplete}
            preload
            autoPopulate={{
              email: formData.email,
              company_name: formData.email.split("@")[1] || null,
              screen_count: mapScreensToCalendly(formData.screens),
              help_with: helpWith,
            }}
            onButtonClick={handleSubmitDataOnZoho}
          />
        </div>

        <p className="text-xs text-text-secondary md:col-span-2">
          We use this only to build your estimate. You&apos;ll book a switch consult next.
        </p>
      </form>
    </>
  );
}
